/**
 * The editor of the `omr` tenant setting: which of the tenant's OMR sheet
 * designs a paper starts from at import, and reads a sheet with when its paper
 * names none. Only an active design is offered, and the server refuses any
 * other. Tenant-only — a sheet id names one tenant's design, so there is no
 * platform default. See omr-reader-target.md §2.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, FormField, Select } from '@shared-ui/components';
import { tenantsApi } from '../../../api/tenantsApi';
import type { SettingEditorProps } from './TenantSettingsTab';

export function OmrDefaultEditor({ tenantId, value, save, saving, actions }: SettingEditorProps<'omr'>) {
    const [draft, setDraft] = useState(value.defaultSheetId ?? '');
    const { data, isLoading } = useQuery({
        queryKey: ['tenant-omr-sheets', tenantId],
        queryFn: () => tenantsApi.omrSheets(tenantId),
    });
    // One entry per sheet: the reader always uses a sheet's active version.
    const active = (data?.designs ?? []).filter(d => d.status === 'active');
    const dirty = draft !== (value.defaultSheetId ?? '');
    // A stored default whose design was since retired still shows, so it can be seen and replaced.
    const stale = !!value.defaultSheetId && !!data && !active.some(d => d.sheetId === value.defaultSheetId);

    return (
        <div className="tenants-form">
            <FormField label="Default sheet">
                <Select value={draft} onChange={e => setDraft(e.target.value)} disabled={isLoading}>
                    <option value="">No default</option>
                    {stale && <option value={value.defaultSheetId}>{value.defaultSheetId} (no active version)</option>}
                    {active.map(d => (
                        <option key={d.sheetId} value={d.sheetId}>{d.name} — {d.sheetId} v{d.version}</option>
                    ))}
                </Select>
                {data && !active.length && (
                    <small>No active design yet — save and activate one with scripts/ops/omrSheetDesign.ts.</small>
                )}
                {stale && <small>The stored default has no active version, so sheets without a pinned design are not read.</small>}
            </FormField>
            <div className="tenant-detail-save">
                {dirty && <span className="tenant-detail-note">Unsaved changes</span>}
                {actions}
                <Button
                    onClick={() => save(draft ? { ...value, defaultSheetId: draft } : null)}
                    loading={saving}
                    disabled={!dirty}
                >
                    Save
                </Button>
            </div>
        </div>
    );
}
